import {
  HALE_BU_ID,
  HALE_APP_ID,
  STUDENT_BU_ID,
  STUDENT_APP_ID,
} from './brandEnv';

/** Staging: hale.sparkl.ac | Production: hale.sparkl.me */
export const HALE_HOST_PATTERNS = ['hale.sparkl.ac', 'hale.sparkl.me'] as const;

export { HALE_BU_ID, HALE_APP_ID, STUDENT_BU_ID, STUDENT_APP_ID };

export const isLocalDevelopment = (): boolean => {
  const hostname = window.location.hostname.toLowerCase();
  return (
    process.env.NODE_ENV === 'development' ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1'
  );
};

export const isHaleUrl = (): boolean => {
  const hostname = window.location.hostname.toLowerCase();
  return HALE_HOST_PATTERNS.some((pattern) => hostname.includes(pattern));
};

const LOCAL_HALE_SESSION_KEY = 'hale_local_session';

/** Local dev: /login?hale shows Hale login preview */
export const isLocalHaleLoginQuery = (): boolean => {
  if (!isLocalDevelopment()) return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('hale');
};

/** Persist Hale test context across post-login navigation (localhost only). */
export const setLocalHaleSession = (): void => {
  if (!isLocalDevelopment()) return;

  try {
    sessionStorage.setItem(LOCAL_HALE_SESSION_KEY, '1');
  } catch {
    // ignore
  }
};

export const clearLocalHaleSession = (): void => {
  try {
    sessionStorage.removeItem(LOCAL_HALE_SESSION_KEY);
  } catch {
    // ignore
  }
};

export const isLocalHaleSession = (): boolean => {
  if (!isLocalDevelopment()) return false;

  try {
    return sessionStorage.getItem(LOCAL_HALE_SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Post-login Hale branding: production subdomain or persisted local test session.
 * Does not use ?hale — login page theme is URL-only (see isHaleLoginPreview).
 */
export const isHaleBrandHost = (): boolean =>
  isHaleUrl() || (isLocalDevelopment() && isLocalHaleSession());

/** Hale login page: hale.sparkl.ac/.me or localhost/login?hale (URL only, not session). */
export const isHaleLoginPreview = (): boolean =>
  isHaleUrl() || isLocalHaleLoginQuery();

export const isStudentUrl = (): boolean => {
  return window.location.hostname.toLowerCase().includes('student.sparkl.');
};

/**
 * Resolves user buId from API. Falls back to brand env buId until API returns bu_id.
 */
export const extractBuIdFromPayload = (
  payload: Record<string, unknown> | null | undefined,
): number | null => {
  if (!payload) return null;

  const nestedUser = payload.user as Record<string, unknown> | undefined;
  const candidates = [
    payload.bu_id,
    payload.buId,
    payload.business_unit_id,
    nestedUser?.bu_id,
    nestedUser?.buId,
    nestedUser?.business_unit_id,
  ];

  for (const value of candidates) {
    if (value == null || value === '') continue;
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }

  return null;
};

export const resolveUserBuId = (
  apiBuId?: number | string | null,
  options?: { fromHaleContext?: boolean },
): number => {
  // Hale theme always uses the Hale brand buId from env, not the user's API bu_id.
  if (options?.fromHaleContext || isHaleBrandHost()) {
    return HALE_BU_ID;
  }

  if (apiBuId != null && apiBuId !== '') {
    const parsed = Number(apiBuId);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return STUDENT_BU_ID;
};
